import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert, Box, Chip, FormControlLabel, IconButton, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import PlaceholderPicker from '../PropertyPanel/PlaceholderPicker';
import { insertAtCursor } from '../PropertyPanel/insertAtCursor';
import UpstreamFieldsHint from './UpstreamFieldsHint';

/**
 * NativeActionInputsForm — Item #7 Fase 2 (13/10/2026).
 *
 * Form dinâmico renderizado a partir do JSON Schema (`input_schema_json`) do slug da ação
 * nativa selecionada. Padrão idêntico ao Zapier/Make/n8n/Workato: admin NÃO vê o schema
 * cru — vê um formulário com 1 campo por `property`, cada campo com tipo apropriado,
 * aceita literal OU `{{placeholder}}`.
 *
 * Tipos suportados (sem débitos):
 *   - string   → TextField + botão {{}} (PlaceholderPicker)
 *   - integer  → TextField number ou string com {{...}} (aceito como válido)
 *   - number   → idem integer
 *   - boolean  → Switch; toggle "Usar variável" troca por TextField livre
 *   - enum     → Select dropdown; toggle "Usar variável" troca por TextField livre
 *   - array    → DynamicChipsInput (chips com X, Enter pra adicionar, {{}} por item)
 *   - object   → recursão (renderiza o form dentro de um card indent, max depth 3)
 *
 * Serialização: componente mantém `values` interno como objeto JS; chama `onChange`
 * passando o objeto completo após cada mudança. Pai converte pra JSON string ao persistir.
 */

type JsonSchema = {
    type?: string;
    required?: string[];
    properties?: Record<string, JsonSchema>;
    items?: JsonSchema;
    enum?: Array<string | number>;
    title?: string;
    description?: string;
    default?: unknown;
    /**
     * 13/10/2026 — Suporte a oneOf: cada branch declara seu próprio `required[]`.
     * Semântica: um dos conjuntos de obrigatórios DEVE estar preenchido. UI renderiza todos
     * os campos com Alert "Preencha UM dos conjuntos abaixo" + Chip no label de cada obrig.
     * Validação é soft (apresenta aviso), backend valida de verdade.
     */
    oneOf?: Array<{ required?: string[] }>;
};

interface Props {
    schema: JsonSchema;
    values: Record<string, unknown>;
    onChange: (values: Record<string, unknown>) => void;
    /** Profundidade atual na recursão — protege contra schemas em loop. */
    depth?: number;
    /**
     * Cell JointJS do inspector atual — opcional. Quando passada, o form ativa a feature
     * de inferência downstream: pra cada campo string/number que referencia uma variável
     * (`{{variable.X}}` ou `@X@`), mostra abaixo chips com os campos reais do shape da
     * ação upstream que gravou essa variável.
     */
    cell?: import('@joint/plus').dia.Cell | null;
}

const MAX_DEPTH = 3;

/**
 * Formatos de placeholder aceitos pelo IPlaceholderResolver (backend). Qualquer campo
 * do form pode receber qualquer um deles — resolver se encarrega em runtime.
 *   {{variable.xxx}} / {{param.xxx}} / {{chat.xxx}} / {{customField.xxx}}  — moderno (padrão)
 *   %nome_do_param%                                                        — parâmetro legado
 *   @nome_da_variavel@                                                     — variável legada
 *   $service->campo$                                                       — custom field legado
 */
const PLACEHOLDER_PATTERNS = [
    /\{\{[^{}]+\}\}/,
    /%[^%]+%/,
    /@[^@]+@/,
    /\$[^$]+\$/,
];
const PURE_PLACEHOLDER_PATTERNS = [
    /^\{\{[^{}]+\}\}$/,
    /^%[^%]+%$/,
    /^@[^@]+@$/,
    /^\$[^$]+\$$/,
];

/** True se a string INTEIRA é um placeholder puro (sem texto literal em volta). */
const isPlaceholderString = (v: unknown): v is string =>
    typeof v === 'string' && PURE_PLACEHOLDER_PATTERNS.some((re) => re.test(v.trim()));

/** True se a string CONTÉM pelo menos um placeholder (misturado ou puro). */
const containsPlaceholder = (v: unknown): boolean =>
    typeof v === 'string' && PLACEHOLDER_PATTERNS.some((re) => re.test(v));

const NativeActionInputsForm = ({ schema, values, onChange, depth = 0, cell = null }: Props): ReactElement => {
    const intl = useIntl();
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | null>(null);
    const activeKeyRef = useRef<string | null>(null);

    const properties = schema?.properties ?? {};
    const required = new Set(schema?.required ?? []);

    // 13/10/2026 — Suporte a oneOf. Semântica: ao menos UM conjunto `required` dentre as
    // branches tem que estar totalmente preenchido. UI renderiza TODOS os campos (uma field
    // por property) + Alert explicativo + marca cada required como "um dos N conjuntos".
    const oneOfBranches = Array.isArray(schema?.oneOf) ? schema.oneOf : [];
    const hasOneOf = oneOfBranches.length > 0;
    const oneOfRequired = hasOneOf
        ? new Set(oneOfBranches.flatMap((b) => b?.required ?? []))
        : new Set<string>();

    // Avalia qual branch (se alguma) está completa — para o Alert mostrar "✅ Validado por..."
    const satisfiedBranch = hasOneOf
        ? oneOfBranches.findIndex((b) =>
            (b?.required ?? []).every((k) => {
                const v = values[k];
                return v !== undefined && v !== null && String(v).trim() !== '';
            })
        )
        : -1;

    const setKey = (key: string, next: unknown) => {
        onChange({ ...values, [key]: next });
    };

    const openPicker = (anchor: HTMLElement, input: HTMLInputElement | null, key: string) => {
        activeInputRef.current = input;
        activeKeyRef.current = key;
        setPickerAnchor(anchor);
    };

    const onPlaceholderInsert = (placeholder: string) => {
        const key = activeKeyRef.current;
        const input = activeInputRef.current;
        if (!key || !input) { setPickerAnchor(null); return; }
        insertAtCursor(input, placeholder);
        // Dispara evento 'input' pra React notar o novo valor.
        const ev = new Event('input', { bubbles: true });
        input.dispatchEvent(ev);
        // Sincroniza com o state (insertAtCursor altera input.value direto).
        setKey(key, input.value);
        setPickerAnchor(null);
    };

    if (depth >= MAX_DEPTH) {
        // Edge case defensivo — schema em loop ou aninhamento absurdo.
        return (
            <Typography variant="caption" color="warning.main">
                {intl.formatMessage({
                    id: 'na.form.depthExceeded',
                    defaultMessage: 'Profundidade de aninhamento máxima atingida — simplifique o schema.',
                })}
            </Typography>
        );
    }

    const keys = Object.keys(properties);
    if (keys.length === 0) {
        return (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                {intl.formatMessage({
                    id: 'na.form.noInputs',
                    defaultMessage: 'Esta ação não requer inputs.',
                })}
            </Typography>
        );
    }

    return (
        <>
            {hasOneOf && (
                <Alert
                    severity={satisfiedBranch >= 0 ? 'success' : 'warning'}
                    icon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ fontSize: 12, py: 0.5, mb: 1 }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        {satisfiedBranch >= 0
                            ? intl.formatMessage({
                                id: 'na.form.oneOf.satisfied',
                                defaultMessage: 'Validação atendida — você preencheu um conjunto obrigatório.',
                            })
                            : intl.formatMessage({
                                id: 'na.form.oneOf.required',
                                defaultMessage: 'Preencha UM dos conjuntos obrigatórios abaixo:',
                            })}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {oneOfBranches.map((b, i) => (
                            <Chip
                                key={i}
                                label={(b?.required ?? []).join(' + ') || `#${i + 1}`}
                                size="small"
                                color={satisfiedBranch === i ? 'success' : 'default'}
                                variant={satisfiedBranch === i ? 'filled' : 'outlined'}
                                sx={{ height: 20, fontSize: 10, fontFamily: 'monospace' }}
                            />
                        ))}
                    </Stack>
                </Alert>
            )}

            <Stack spacing={1.5}>
                {keys.map((key) => {
                    const prop = properties[key];
                    // Required = obrigatório clássico OU participante do oneOf (marca pra orientar admin).
                    const isRequired = required.has(key) || oneOfRequired.has(key);
                    const value = values[key];
                    return (
                        <FieldRenderer
                            key={key}
                            fieldKey={key}
                            schema={prop}
                            required={isRequired}
                            value={value}
                            onChange={(next) => setKey(key, next)}
                            onOpenPicker={(anchor, input) => openPicker(anchor, input, key)}
                            depth={depth}
                            cell={cell}
                        />
                    );
                })}
            </Stack>

            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                onInsert={onPlaceholderInsert}
            />
        </>
    );
};

export default NativeActionInputsForm;

// ===========================================================================
// FieldRenderer — dispatcher por tipo
// ===========================================================================

interface FieldRendererProps {
    fieldKey: string;
    schema: JsonSchema;
    required: boolean;
    value: unknown;
    onChange: (next: unknown) => void;
    onOpenPicker: (anchor: HTMLElement, input: HTMLInputElement | null) => void;
    depth: number;
    cell?: import('@joint/plus').dia.Cell | null;
}

const FieldRenderer = (props: FieldRendererProps): ReactElement => {
    const { schema, fieldKey, required } = props;
    const label = (schema?.title ?? fieldKey) + (required ? ' *' : '');
    const help = schema?.description;

    // Enum → Select (com escape pra placeholder)
    if (Array.isArray(schema?.enum) && schema.enum.length > 0) {
        return <EnumField {...props} label={label} help={help} />;
    }

    switch (schema?.type) {
        case 'boolean':
            return <BooleanField {...props} label={label} help={help} />;
        case 'integer':
        case 'number':
            return <NumericField {...props} label={label} help={help} />;
        case 'array':
            return <ArrayField {...props} label={label} help={help} />;
        case 'object':
            return <ObjectField {...props} label={label} help={help} />;
        case 'string':
        default:
            return <StringField {...props} label={label} help={help} />;
    }
};

// ===========================================================================
// StringField
// ===========================================================================

interface FieldSubProps extends FieldRendererProps {
    label: string;
    help?: string;
}

const StringField = ({ value, onChange, onOpenPicker, label, help, cell }: FieldSubProps): ReactElement => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const intl = useIntl();
    const strValue = typeof value === 'string' ? value : (value == null ? '' : String(value));
    return (
        <div>
            <TextField
                size="small"
                label={label}
                fullWidth
                value={strValue}
                onChange={(e) => onChange(e.target.value)}
                helperText={help}
                inputRef={(el) => { inputRef.current = el; }}
                InputProps={{
                    endAdornment: (
                        <Tooltip title={intl.formatMessage({ id: 'na.form.pickVariable', defaultMessage: 'Inserir variável/placeholder' })}>
                            <IconButton
                                size="small"
                                onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                                sx={{ p: 0.25 }}
                            >
                                <CodeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ),
                }}
            />
            {cell && (
                <UpstreamFieldsHint
                    cell={cell}
                    textValue={strValue}
                    compact
                    onInsert={(token) => {
                        const el = inputRef.current;
                        if (el) {
                            insertAtCursor(el, token);
                            onChange(el.value);
                        } else {
                            onChange((strValue ?? '') + token);
                        }
                    }}
                />
            )}
        </div>
    );
};

// ===========================================================================
// NumericField — aceita integer/number ou {{placeholder}}
// ===========================================================================

const NumericField = ({ value, onChange, onOpenPicker, label, help, schema, cell }: FieldSubProps): ReactElement => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const intl = useIntl();
    const stringValue = typeof value === 'string' ? value : (value == null ? '' : String(value));
    // Validação: aceita vazio, qualquer um dos 4 formatos de placeholder do Mexx
    // ({{}}, %%, @@, $$), ou número válido pelo tipo. Placeholders misturados com
    // literal (ex.: "ID %Número%") também passam — resolver trata em runtime.
    const isValid = (() => {
        if (!stringValue) return true;
        if (containsPlaceholder(stringValue)) return true;
        if (schema?.type === 'integer') return /^-?\d+$/.test(stringValue.trim());
        return /^-?\d*\.?\d+$/.test(stringValue.trim());
    })();

    return (
        <div>
            <TextField
                size="small"
                label={label}
                fullWidth
                value={stringValue}
                onChange={(e) => onChange(e.target.value)}
                error={!isValid}
                helperText={!isValid
                    ? intl.formatMessage({
                        id: 'na.form.numericInvalid',
                        defaultMessage: 'Use número ou um placeholder {{variable.xxx}}.',
                    })
                    : help}
                inputRef={(el) => { inputRef.current = el; }}
                InputProps={{
                    endAdornment: (
                        <Tooltip title={intl.formatMessage({ id: 'na.form.pickVariable', defaultMessage: 'Inserir variável/placeholder' })}>
                            <IconButton
                                size="small"
                                onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                                sx={{ p: 0.25 }}
                            >
                                <CodeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ),
                }}
            />
            {cell && (
                <UpstreamFieldsHint
                    cell={cell}
                    textValue={stringValue}
                    compact
                    onInsert={(token) => {
                        const el = inputRef.current;
                        if (el) {
                            insertAtCursor(el, token);
                            onChange(el.value);
                        } else {
                            onChange((stringValue ?? '') + token);
                        }
                    }}
                />
            )}
        </div>
    );
};

// ===========================================================================
// BooleanField — Switch ou TextField livre (quando valor é placeholder)
// ===========================================================================

const BooleanField = ({ value, onChange, onOpenPicker, label, help }: FieldSubProps): ReactElement => {
    const intl = useIntl();
    const asString = typeof value === 'string' ? value : String(value ?? '');
    const [useVar, setUseVar] = useState<boolean>(isPlaceholderString(asString));
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                    {label}
                </Typography>
                <FormControlLabel
                    control={<Switch
                        size="small"
                        checked={useVar}
                        onChange={(_, next) => {
                            setUseVar(next);
                            // Reset do valor ao trocar de modo
                            onChange(next ? '' : false);
                        }}
                    />}
                    label={<Typography variant="caption">{intl.formatMessage({
                        id: 'na.form.useVariable',
                        defaultMessage: 'Usar variável',
                    })}</Typography>}
                    sx={{ m: 0 }}
                />
            </Stack>

            {useVar
                ? (
                    <TextField
                        size="small"
                        fullWidth
                        value={asString}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="{{variable.xxx}}"
                        helperText={help}
                        inputRef={(el) => { inputRef.current = el; }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    size="small"
                                    onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                                    sx={{ p: 0.25 }}
                                >
                                    <CodeIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                )
                : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                            size="small"
                            checked={value === true}
                            onChange={(_, next) => onChange(next)}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {value === true
                                ? intl.formatMessage({ id: 'na.form.true', defaultMessage: 'Verdadeiro' })
                                : intl.formatMessage({ id: 'na.form.false', defaultMessage: 'Falso' })}
                        </Typography>
                    </Stack>
                )}

            {help && !useVar && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {help}
                </Typography>
            )}
        </Box>
    );
};

// ===========================================================================
// EnumField — Select com toggle "usar variável"
// ===========================================================================

const EnumField = ({ value, onChange, onOpenPicker, label, help, schema }: FieldSubProps): ReactElement => {
    const intl = useIntl();
    const options = schema?.enum ?? [];
    const asString = typeof value === 'string' ? value : (value == null ? '' : String(value));
    const [useVar, setUseVar] = useState<boolean>(isPlaceholderString(asString));
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{label}</Typography>
                <FormControlLabel
                    control={<Switch
                        size="small"
                        checked={useVar}
                        onChange={(_, next) => {
                            setUseVar(next);
                            onChange(next ? '' : (options[0] ?? ''));
                        }}
                    />}
                    label={<Typography variant="caption">{intl.formatMessage({
                        id: 'na.form.useVariable',
                        defaultMessage: 'Usar variável',
                    })}</Typography>}
                    sx={{ m: 0 }}
                />
            </Stack>

            {useVar
                ? (
                    <TextField
                        size="small"
                        fullWidth
                        value={asString}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="{{variable.xxx}}"
                        helperText={help}
                        inputRef={(el) => { inputRef.current = el; }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    size="small"
                                    onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                                    sx={{ p: 0.25 }}
                                >
                                    <CodeIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                )
                : (
                    <>
                        <Select
                            size="small"
                            fullWidth
                            value={options.includes(value as any) ? (value as any) : ''}
                            onChange={(e) => onChange(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value=""><em>{intl.formatMessage({
                                id: 'na.form.selectOption',
                                defaultMessage: 'Selecione...',
                            })}</em></MenuItem>
                            {options.map((opt) => (
                                <MenuItem key={String(opt)} value={opt as any}>{String(opt)}</MenuItem>
                            ))}
                        </Select>
                        {help && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                {help}
                            </Typography>
                        )}
                    </>
                )}
        </Box>
    );
};

// ===========================================================================
// ArrayField — DynamicChipsInput inline
// ===========================================================================

const ArrayField = ({ value, onChange, onOpenPicker, label, help, schema }: FieldSubProps): ReactElement => {
    const intl = useIntl();
    const items: unknown[] = Array.isArray(value) ? value : [];
    const [draft, setDraft] = useState<string>('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const itemType = schema?.items?.type ?? 'string';
    const itemsEnum = schema?.items?.enum;

    const normalizeItem = (raw: string): unknown => {
        const trimmed = raw.trim();
        if (!trimmed) return null;
        if (isPlaceholderString(trimmed)) return trimmed;
        if (itemType === 'integer') {
            const n = parseInt(trimmed, 10);
            return Number.isFinite(n) ? n : trimmed;
        }
        if (itemType === 'number') {
            const n = parseFloat(trimmed);
            return Number.isFinite(n) ? n : trimmed;
        }
        if (itemType === 'boolean') {
            return trimmed === 'true';
        }
        return trimmed;
    };

    const addItem = () => {
        const next = normalizeItem(draft);
        if (next === null || next === '') return;
        onChange([...items, next]);
        setDraft('');
    };

    const removeAt = (index: number) => {
        onChange(items.filter((_, i) => i !== index));
    };

    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{label}</Typography>

            {items.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {items.map((it, i) => (
                        <Chip
                            key={`${i}-${String(it)}`}
                            label={String(it)}
                            size="small"
                            onDelete={() => removeAt(i)}
                            sx={{
                                background: '#8BC34A',
                                color: '#fff',
                                fontWeight: 500,
                                '& .MuiChip-deleteIcon': {
                                    color: 'rgba(255,255,255,0.85)',
                                    '&:hover': { color: '#fff' },
                                },
                            }}
                        />
                    ))}
                </Stack>
            )}

            {itemsEnum
                ? (
                    <Stack direction="row" spacing={1}>
                        <Select
                            size="small"
                            value={draft}
                            onChange={(e) => setDraft(String(e.target.value))}
                            displayEmpty
                            sx={{ flex: 1 }}
                        >
                            <MenuItem value=""><em>{intl.formatMessage({
                                id: 'na.form.selectOption',
                                defaultMessage: 'Selecione...',
                            })}</em></MenuItem>
                            {itemsEnum.map((opt) => (
                                <MenuItem key={String(opt)} value={String(opt)}>{String(opt)}</MenuItem>
                            ))}
                        </Select>
                        <IconButton size="small" color="primary" onClick={addItem} disabled={!draft}>
                            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
                        </IconButton>
                    </Stack>
                )
                : (
                    <Stack direction="row" spacing={0.5}>
                        <TextField
                            size="small"
                            fullWidth
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && draft.trim()) {
                                    e.preventDefault();
                                    addItem();
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: 'na.form.array.placeholder',
                                defaultMessage: 'Digite e Enter — ou use {{variable}}',
                            })}
                            inputRef={(el) => { inputRef.current = el; }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                                        sx={{ p: 0.25 }}
                                    >
                                        <CodeIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                        <IconButton size="small" color="primary" onClick={addItem} disabled={!draft.trim()}>
                            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
                        </IconButton>
                    </Stack>
                )}

            {help && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {help}
                </Typography>
            )}
        </Box>
    );
};

// ===========================================================================
// ObjectField — recursão
// ===========================================================================

const ObjectField = ({ value, onChange, label, help, schema, depth }: FieldSubProps): ReactElement => {
    const intl = useIntl();
    const current = (typeof value === 'object' && value !== null && !Array.isArray(value))
        ? (value as Record<string, unknown>)
        : {};
    const [expanded, setExpanded] = useState<boolean>(true);

    return (
        <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1,
            background: '#fafafa',
        }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: expanded ? 1 : 0 }}>
                <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.25 }}>
                    <span style={{
                        display: 'inline-block',
                        transform: `rotate(${expanded ? 90 : 0}deg)`,
                        transition: 'transform 150ms',
                        fontSize: 10,
                    }}>▶</span>
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{label}</Typography>
                {Object.keys(current).length > 0 && (
                    <Tooltip title={intl.formatMessage({
                        id: 'na.form.object.clear',
                        defaultMessage: 'Limpar',
                    })}>
                        <IconButton size="small" onClick={() => onChange({})} sx={{ p: 0.25 }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {expanded && (
                <Box sx={{ pl: 1.5, borderLeft: '2px solid #D4D4D4' }}>
                    <NativeActionInputsForm
                        schema={schema}
                        values={current}
                        onChange={(next) => onChange(next)}
                        depth={depth + 1}
                    />
                </Box>
            )}

            {help && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {help}
                </Typography>
            )}
        </Box>
    );
};
